import { Button, Text } from '@react-spectrum/s2';
import { useCallback, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { SuccessInterstitial } from '@/spaces/chat/panels/SuccessInterstitial';
import { trackEvent } from '@/utils/fullstory';

import type { Artifact } from '../../../../bridge/types/artifact.ts';

type Args = {
	artifact: Artifact;
	title: string;
	summary: string;
	onClose: () => void;
	// 'process' (default) activates the whole process and offers a "View job"
	// button. 'artifact' activates only this artifact (publish-only) and shows a
	// Close-only interstitial — for reviews that aren't jobs (e.g. IG replies).
	target?: 'process' | 'artifact';
};

export const useJobActivation = ({
	artifact,
	title,
	summary,
	onClose,
	target = 'process',
}: Args) => {
	const bridgeApi = useBridgeApi();
	const openChatPanel = useOpenChatPanel();
	const [activated, setActivated] = useState(false);
	const [discardedPress, setDiscardedPress] = useState(false);

	// A plan edit made this campaign stale, so the server threw its Process away.
	// `do_activate_process_reducer` returns no deltas for it, and this hook declared success
	// regardless: the owner pressed Activate on an open panel and read "Comment watcher
	// activated" while nothing ran and no error ever arrived. `CampaignSetupPanel` reads the same
	// marker to say the same thing on the setup card.
	const discarded =
		target === 'process' &&
		!!artifact.process_id &&
		bridgeApi.user_state?.process_setup_discarded?.[artifact.process_id] ===
			true;

	// A discarded campaign is normally being rebuilt. One the owner DELETED is not coming back,
	// and the rebuild sentence would promise a setup card that never arrives. `CampaignSetupPanel`
	// reads the same pair to tell the two states apart, and this panel outlives the discard: the
	// cleanup closes panels keyed by PROCESS id, while an artifact panel is keyed by artifact id.
	const removed =
		!!artifact.process_id &&
		bridgeApi.user_state?.process_setup_removed?.[artifact.process_id] === true;

	const handleActivate = useCallback(() => {
		if (discarded) {
			setDiscardedPress(true);
			return;
		}
		trackEvent('Process Activated', {
			artifact_id: artifact.id,
			...(artifact.process_id ? { process_id: artifact.process_id } : {}),
		});
		setActivated(true);
		if (target === 'artifact') {
			bridgeApi.sendAction({
				type: 'do_activate_artifact',
				artifact_id: artifact.id,
			});
		} else if (artifact.process_id) {
			bridgeApi.sendAction({
				type: 'do_activate_process',
				process_id: artifact.process_id,
			});
		}
	}, [artifact.id, artifact.process_id, bridgeApi, discarded, target]);

	const handleViewJob = useCallback(() => {
		if (!artifact.process_id) return;
		openChatPanel({
			panel_type: 'artifact',
			artifact_type: 'job',
			item_id: artifact.process_id,
		});
	}, [artifact.process_id, openChatPanel]);

	// Never the success interstitial. That view fires confetti and draws a green tick, so it would
	// celebrate the same non-event in a different font. One true sentence instead, in the wording
	// the setup card already uses for this state.
	const discardedView = discardedPress ? (
		<div
			data-testid={
				removed ? 'job-activation-removed' : 'job-activation-discarded'
			}
			className="flex h-full w-full flex-col items-center justify-center gap-4
				px-8 text-center"
		>
			<p className="text-sm text-(--text-secondary)">
				{removed
					? 'This campaign was removed from your plan.'
					: 'This campaign is being set up again, from your edited plan. You can start it when the new setup card arrives.'}
			</p>
			<Button variant="secondary" onPress={onClose}>
				<Text>Close</Text>
			</Button>
		</div>
	) : null;

	const activatedView =
		discardedView ??
		(activated ? (
			<SuccessInterstitial
				title={title}
				summary={summary}
				{...(target === 'artifact'
					? { primaryAction: { label: 'Close', onPress: onClose } }
					: {
							secondaryAction: { label: 'Close', onPress: onClose },
							primaryAction: { label: 'View job', onPress: handleViewJob },
						})}
			/>
		) : null);

	return { activated, handleActivate, activatedView };
};
